// src/components/InstitutionsTable.jsx
import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Icon,
  Loader,
  Message,
  Confirm,
  Header,
  Image,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { InstitutionApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { image } from "../../../../assets"; // placeholder avatar

const institutionController = new InstitutionApi();

export function InstitutionsTable({ active, reload, onReload, onEdit }) {
  const { t } = useTranslation(["institution", "common"]);
  const { accessToken, user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    setData(null);
    institutionController
      .getInstitutions(accessToken, user.user._id, active)
      .then((res) => {
        // Filtrar solo instituciones Revi
        const onlyRevi = (res.institutions || []).filter(
          (inst) => inst.role === "revi",
        );
        setData(onlyRevi);
      })
      .catch((err) => {
        setError(err.message);
        setData([]);
      });
  }, [accessToken, user.user._id, active, reload]);

  if (data === null) return <Loader active inline="centered" />;
  if (error) return <Message error content={error} />;
  if (!data.length)
    return <Message info content={t("partner.institutions.empty")} />;

  return (
    <>
      <Table celled compact selectable striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {t("partner.institutions.columnInstitution")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("partner.institutions.columnPlan")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("partner.institutions.columnPatientLicenses")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("partner.institutions.columnTherapistLicenses")}
            </Table.HeaderCell>
            <Table.HeaderCell>{t("common:fields.status")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="center">
              {t("common:fields.actions")}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((inst) => (
            <Table.Row
              key={inst._id}
              positive={inst.active}
              negative={!inst.active}
            >
              {/* Nombre con avatar */}
              <Table.Cell>
                <Header as="h4" image>
                  <Image
                    src={inst.avatar || image.noAvatar}
                    rounded
                    size="mini"
                  />
                  <Header.Content>{inst.institutionName}</Header.Content>
                </Header>
              </Table.Cell>

              {/* Plan */}
              <Table.Cell>
                <Header as="h4" image>
                  <Image
                    src={inst.planEduca?.miniature || image.noAvatar}
                    rounded
                    size="mini"
                  />
                  <Header.Content>{inst.planEduca?.title}</Header.Content>
                </Header>
              </Table.Cell>

              {/* Licencias */}
              <Table.Cell>{inst.pacientesN}</Table.Cell>
              <Table.Cell>{inst.terapeutasN}</Table.Cell>

              {/* Estado activo/inactivo */}
              <Table.Cell>
                {inst.active
                  ? t("partner.institutions.active")
                  : t("partner.institutions.inactive")}
              </Table.Cell>

              {/* Acciones */}
              <Table.Cell textAlign="center">
                <Button
                  icon
                  as="a"
                  href={inst.website}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="eye" />
                </Button>
                <Button icon primary onClick={() => onEdit(inst)}>
                  <Icon name="pencil" />
                </Button>
                <Button
                  icon
                  color={inst.active ? "orange" : "teal"}
                  onClick={() => {
                    setCurrent(inst);
                    setConfirmOpen(true);
                  }}
                >
                  <Icon name={inst.active ? "ban" : "check"} />
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Confirm
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          institutionController
            .toggleInstitutionActivation(
              accessToken,
              current._id,
              !current.active,
            )
            .then(() => {
              onReload();
              setConfirmOpen(false);
            })
            .catch(() => setConfirmOpen(false));
        }}
        // Dos frases enteras y no un verbo intercambiable dentro de una: en
        // frances "désactiver" y "activer" no se construyen igual.
        content={t(
          current?.active
            ? "partner.institutions.confirmDesactivar"
            : "partner.institutions.confirmActivar",
          { centro: current?.institutionName },
        )}
      />
    </>
  );
}
